










































import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  ssrRef,
} from "@nuxtjs/composition-api";
import { CacheTagPrefix, useCache } from "@vue-storefront/cache";
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";
import { useCategory, useFacet, useUiHelpers } from "~/composables";
import { usePageStore } from "~/stores/page";

import { useAddToCart } from "~/helpers/cart/addToCart";
import { getMetaInfo } from "~/helpers/getMetaInfo";
import { useCategoryContent } from "~/modules/catalog/category/components/cms/useCategoryContent";
import facetGetters from "~/modules/catalog/category/getters/facetGetters";
import { useTraverseCategory } from "~/modules/catalog/category/helpers/useTraverseCategory";
import { useWishlist } from "~/modules/wishlist/composables/useWishlist";

import CategoryBreadcrumbs from "~/modules/catalog/category/components/breadcrumbs/CategoryBreadcrumbs.vue";
import CategoryNavbar from "~/modules/catalog/category/components/navbar/CategoryNavbar.vue";

import type { MetaInfo } from "vue-meta";
import type { ProductInterface } from "~/modules/GraphQL/types";
import type { SortingModel } from "~/modules/catalog/category/composables/useFacet/sortingOptions";
import type { Product } from "~/modules/catalog/product/types";

const ITEMS_PER_PAGE = 50;
const SCROLL_THRESHOLD = 2500;

export default defineComponent({
  name: "CategoryPage",
  components: {
    ShowEmptyResults: () =>
      import(
        "~/components/JetUi/molecules/ShowEmptyResults/ShowEmptyResults.vue"
      ),
    CategoryFilters: () =>
      import(
        "~/modules/catalog/category/components/filters/CategoryFilters.vue"
      ),
    CmsContent: () =>
      import("~/modules/catalog/category/components/cms/CmsContent.vue"),
    CategoryProductGrid: () =>
      import(
        "~/modules/catalog/category/components/views/CategoryProductGrid.vue"
      ),
    CategoryNavbar,
    CategoryBreadcrumbs,
    SkeletonLoader,
  },
  transition: "fade",
  setup() {
    const { routeData } = usePageStore();
    const { getContentData } = useCategoryContent();
    const { loadCategoryMeta } = useCategory();
    const { addTags } = useCache();
    const uiHelpers = useUiHelpers();
    const cmsContent = ref("");
    const isShowCms = ref(false);
    const isShowProducts = ref(false);
    const products = ssrRef<ProductInterface[]>([]);
    const page = ref(1);
    const sortBy = ref<SortingModel>({ selected: "", options: [] });

    const {
      load: loadWishlist,
      addItem: addItemToWishlistBase,
      isInWishlist,
      removeItem: removeItemFromWishlist,
    } = useWishlist();
    const { result, search } = useFacet();
    const { addItemToCart } = useAddToCart();

    const categoryMeta = ref(null);
    const shouldShow = ref(false);
    const stopRequest = ref(false);

    const addItemToWishlist = async (product: Product) => {
      await (isInWishlist({ product })
        ? removeItemFromWishlist({ product })
        : addItemToWishlistBase({ product }));
    };

    const { activeCategory, loadCategoryTree } = useTraverseCategory();
    const activeCategoryName = computed(() => activeCategory.value?.name ?? "");

    const categoryUid = routeData.uid;

    const handleScroll = () => {
      if (!isShowProducts.value || stopRequest.value) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD) {
        fetchProducts();
      }
    };

    const fetchProducts = async () => {
      isShowProducts.value = false;

      if (!activeCategory.value) {
        await loadCategoryTree();
      }

      const searchParams = {
        ...uiHelpers.getFacetsFromURL(),
        category_uid: categoryUid,
        page: page.value,
        itemsPerPage: ITEMS_PER_PAGE,
      };

      try {
        const [content, categoryMetaData] = await Promise.all([
          getContentData(categoryUid as string),
          loadCategoryMeta({ category_uid: routeData.value?.uid }),
          search(searchParams),
        ]);

        categoryMeta.value = categoryMetaData;
        cmsContent.value = content?.cmsBlock?.content ?? "";
        isShowCms.value = content.isShowCms;
        isShowProducts.value = content.isShowProducts;

        if (!result.value.data.items.length) {
          stopRequest.value = true;
        }

        const newProducts = facetGetters.getProducts(result.value) ?? [];
        products.value = [...products.value, ...newProducts];
        sortBy.value = facetGetters.getSortOptions(result.value);

        const tags = [{ prefix: CacheTagPrefix.View, value: routeData.uid }];
        const productTags = products.value.map((product) => ({
          prefix: CacheTagPrefix.Product,
          value: product.uid,
        }));

        addTags([...tags, ...productTags]);

        page.value++;
      } catch (error) {
        console.error("Failed to fetch products:", error);
        stopRequest.value = true;
      } finally {
        isShowProducts.value = true;
      }
    };

    onMounted(async () => {
      await fetchProducts();
      await loadWishlist();
      shouldShow.value = true;
      window.addEventListener("scroll", handleScroll, { passive: true });
    });

    const onReloadProducts = async () => {
      shouldShow.value = false;
      stopRequest.value = false;
      page.value = 1;
      products.value = [];

      await fetchProducts();
      shouldShow.value = true;
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const toTitleCase = (title: string) =>
      title.replace(/\w\S*/g, (txt) => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
      });

    const title = computed(() => toTitleCase(activeCategoryName.value) || "");

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    return {
      ...uiHelpers,
      addItemToCart,
      addItemToWishlist,
      products,
      sortBy,
      isShowCms,
      isShowProducts,
      cmsContent,
      activeCategoryName,
      routeData,
      categoryMeta,
      onReloadProducts,
      title,
      shouldShow,
      stopRequest,
    };
  },
  head(): MetaInfo {
    const metaInfo = getMetaInfo(this.categoryMeta) as MetaInfo;
    return {
      ...metaInfo,
      title: this.title as string,
    };
  },
});
